import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import { Body1, Body2, File, SectionHeader } from '../../blocks';
import { OnwardRadio } from '@/components/ShipmentForm/blocks';
import { captureException } from '@sentry/react';
import uploadPhotoAsync from '../../../../utilities/uploadPhotoAsync';
import FileDropZone from '@/components/OrderDetailsPage/FileDropZone';
import { ITEM_TYPE_LABELS, ITEM_TYPE_REASONS } from '../../constants';
import { colors } from '@/styles';
import { Clear } from '@material-ui/icons';
import { HR } from '@/components/Resources/blocks';

const ClaimItem = ({ claim, claim_item, callbacks, index }) => {
    const handleFileAdd = async (attribute, filesArray) => {
        const uploadedFiles = [];

        try {
            for (const { file, fileType } of filesArray) {
                const fileUrl = URL.createObjectURL(file);
                const fbFileUrl = await uploadPhotoAsync(fileUrl, `claims/${attribute}`, fileType);
                uploadedFiles.push(fbFileUrl);
            }

            callbacks.updateItem({ ...claim_item, [attribute]: [...(claim_item[attribute] || []), ...uploadedFiles] });
        } catch (e) {
            console.error(e);
            captureException(e);
        }
    };

    const handleFileDelete = async (attribute, index) => {
        callbacks.updateItem({ ...claim_item, [attribute]: claim_item[attribute].filter((_, i) => i !== index) });
    };

    const affectedItems = useMemo(() => {
        const items = claim?.order?.itemsByOrderId || [];
        const removedItems = (claim?.order?.exceptions || [])
            .filter((excMapping) => excMapping?.exception?.resolution === 'OMIT_ITEM')
            .map((excMapping) => excMapping.item);
        return [...items, ...removedItems];
    }, [claim?.order]);

    if (!claim?.claim_type || !claim?.order) return null;

    return (
        <>
            <hr />
            <Grid
                container
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                <SectionHeader>Item {index + 1}</SectionHeader>
            </Grid>
            <Grid
                container
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                <Grid
                    item
                    xs={6}
                    css={css`
                        padding-right: 0.5rem;
                    `}
                >
                    <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label="What happened? (required)"
                        value={claim_item.reason || ''}
                        onChange={(e) => callbacks.updateItem({ ...claim_item, reason: e.target.value })}
                        error={!claim_item.reason}
                    >
                        {ITEM_TYPE_REASONS[claim.claim_type].map((reason) => (
                            <MenuItem key={reason} value={reason}>
                                {reason}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid
                    item
                    xs={6}
                    css={css`
                        padding-left: 0.5rem;
                    `}
                >
                    <CurrencyTextField
                        fullWidth
                        variant="outlined"
                        label={`Requested Amount for ${ITEM_TYPE_LABELS[claim.claim_type]} (required)`}
                        value={claim_item.amount || ''}
                        onChange={(_, val) => callbacks.updateItem({ ...claim_item, amount: val })}
                        currencySymbole="$"
                        outputFormat="number"
                        error={!(claim_item.amount >= 0)}
                    />
                </Grid>
            </Grid>
            {claim.claim_type === 'FREIGHT' && (
                <Grid
                    container
                    direction="column"
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <Body1 error={!claim_item.item_id}>Which item was affected? (required)</Body1>
                    {affectedItems?.map((item) => (
                        <OnwardRadio
                            key={item.item_id}
                            value={item.item_id}
                            label={
                                <Grid direction="column">
                                    <Grid item>
                                        <Body1>{[item.sku, item.description].filter((x) => !!x).join(' / ')}</Body1>
                                    </Grid>
                                    <Grid item>
                                        <Body2>
                                            {item.total_weight}lbs {item.length}L {item.width}W {item.height}H
                                        </Body2>
                                    </Grid>
                                </Grid>
                            }
                            checked={claim_item.item_id === item.item_id}
                            onChange={(e) => callbacks.updateItem({ ...claim_item, item_id: e.target.value })}
                        />
                    ))}
                </Grid>
            )}
            <Grid
                container
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                <TextField
                    multiline
                    minRows={3}
                    maxRows={5}
                    fullWidth
                    variant="outlined"
                    label="Additional Details / Reason for Claim (required)"
                    value={claim_item.description || ''}
                    onChange={(e) => callbacks.updateItem({ ...claim_item, description: e.target.value })}
                    error={!claim_item.description}
                />
            </Grid>
            {['FREIGHT', 'PROPERTY_DAMAGE'].includes(claim?.claim_type) ? (
                <>
                    <Grid
                        container
                        direction="column"
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    >
                        <Grid item>
                            <Body1>Pictures</Body1>
                        </Grid>
                        <Grid item>
                            <Body2>This helps access damage or incorrect assembling</Body2>
                        </Grid>
                        <Grid
                            container
                            css={css`
                                gap: 12px;
                            `}
                        >
                            <FileDropZone
                                type="multiple"
                                customLabel="Add Pictures"
                                handleFileAdd={(files) => handleFileAdd('images', files)}
                                error={claim_item.reason?.includes('damage') && !claim_item.images?.length}
                            />
                            {(claim_item.images || []).map((image, i) => (
                                <Grid item key={i} className="d-flex position-relative">
                                    <div
                                        style={{
                                            backgroundColor: 'black',
                                            cursor: 'pointer',
                                            opacity: '0.6',
                                            height: '25px',
                                            width: '25px',
                                            position: 'absolute',
                                            top: '5px',
                                            right: '5px',
                                            borderRadius: '50%',
                                            zIndex: 2,
                                        }}
                                    >
                                        <Clear
                                            style={{
                                                position: 'relative',
                                                zIndex: 3,
                                                top: '-2px',
                                                left: '1px',
                                                color: 'white',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleFileDelete('images', i)}
                                        />
                                    </div>
                                    <File key={i} file={image} />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction="column"
                        css={css`
                            margin-bottom: 1rem;
                        `}
                    >
                        <Grid
                            item
                            css={css`
                                ${!claim_item.documents?.length ? `color: ${colors.reds.primary};` : ''}
                            `}
                        >
                            <Body1>Documents (required)</Body1>
                        </Grid>
                        <Grid item>
                            <Body2
                                css={css`
                                    ${!claim_item.documents?.length ? `color: ${colors.reds.primary};` : ''}
                                `}
                            >
                                Include supporting documentation such as an estimate or invoice for item replacement
                            </Body2>
                        </Grid>
                        <Grid
                            container
                            css={css`
                                gap: 12px;
                            `}
                        >
                            <FileDropZone
                                type="multiple"
                                customLabel="Add Documents"
                                handleFileAdd={(files) => handleFileAdd('documents', files)}
                                error={!claim_item.documents?.length}
                            />
                            {(claim_item.documents || []).map((document, i) => (
                                <Grid item key={i} className="d-flex position-relative">
                                    <div
                                        style={{
                                            backgroundColor: 'black',
                                            cursor: 'pointer',
                                            opacity: '0.6',
                                            height: '25px',
                                            width: '25px',
                                            position: 'absolute',
                                            top: '5px',
                                            right: '5px',
                                            borderRadius: '50%',
                                            zIndex: 2,
                                        }}
                                    >
                                        <Clear
                                            style={{
                                                position: 'relative',
                                                zIndex: 3,
                                                top: '-2px',
                                                left: '1px',
                                                color: 'white',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleFileDelete('documents', i)}
                                        />
                                    </div>
                                    <File key={i} file={document} />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </>
            ) : null}
        </>
    );
};

export default ClaimItem;
